import { useEffect, useState, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowUpRight, Sparkles } from "lucide-react";
import { resolveImageUrl } from "../admin/ImageUpload";
import { api } from "../../lib/api";

/* ---------- Word-by-word animated text ---------- */
const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
  exit: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
};

const wordVariants = {
  hidden: { opacity: 0, y: 28, filter: "blur(8px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -16, filter: "blur(6px)", transition: { duration: 0.35 } },
};

const AnimatedWords = ({ text, className = "", "data-testid": testid }) => (
  <motion.span variants={containerVariants} initial="hidden" animate="show" exit="exit" className={className} data-testid={testid}>
    {(text || "").split(" ").map((w, i) => (
      <motion.span key={i} variants={wordVariants} className="inline-block whitespace-pre">
        {w + " "}
      </motion.span>
    ))}
  </motion.span>
);

export default function HeroCarousel({ slides }) {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [autoplayMs, setAutoplayMs] = useState(3000);

  useEffect(() => {
    let cancelled = false;
    api.get("/site-settings")
      .then((res) => {
        if (cancelled) return;
        const ms = Number(res?.data?.hero_autoplay_ms);
        if (Number.isFinite(ms) && ms >= 0) setAutoplayMs(ms);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % slides.length);
    setSubIndex(0);
  }, [slides.length]);
  const prev = () => {
    setIndex((i) => (i - 1 + slides.length) % slides.length);
    setSubIndex(0);
  };

  useEffect(() => {
    if (!slides.length || paused || autoplayMs <= 0) return;
    const t = setInterval(next, autoplayMs);
    return () => clearInterval(t);
  }, [next, slides.length, paused, autoplayMs]);

  const slide = slides[index];
  const subImages = useMemo(() => {
    if (!slide) return [];
    return (slide.images && slide.images.length ? slide.images : [slide.image_url]).filter(Boolean);
  }, [slide]);

  useEffect(() => {
    if (subImages.length <= 1 || paused || autoplayMs <= 0) return;
    const t = setInterval(() => setSubIndex((i) => (i + 1) % subImages.length), autoplayMs);
    return () => clearInterval(t);
  }, [subImages.length, index, paused, autoplayMs]);

  if (!slides?.length) return null;

  const activeImage = resolveImageUrl(subImages[subIndex] || slide.image_url);
  const slideKey = slide.id || slide.title;
  const pos = (slide.text_position || "left").toLowerCase();
  const posClass = pos === "center" ? "justify-center" : pos === "right" ? "justify-end" : "justify-start";

  return (
    <section
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative mt-24 h-[calc(100vh-6rem)] min-h-[640px] w-full overflow-hidden bg-[#EFF6FF]"
    >
      {/* Fullbleed background image — Ken-Burns slow zoom */}
      <AnimatePresence mode="wait">
        <motion.img
          key={slideKey + "-bg-" + subIndex}
          src={activeImage}
          alt={slide.title}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover animate-kenburns"
        />
      </AnimatePresence>

      {/* Subtle gradient overlay for text legibility on transparent placeholder */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 via-[#0F172A]/25 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/40 via-transparent to-transparent pointer-events-none" />
      {/* Vibrant blue blobs for brand accent */}
      <div className="absolute -top-20 -left-10 w-[420px] h-[420px] blob blob-blue opacity-30 pointer-events-none" />
      <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] blob blob-cyan opacity-30 pointer-events-none" />

      {/* Content — text written directly on the carousel image (NO placeholder card). */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-start pt-8 pb-28">
        <div data-testid="hero-card" className="w-full max-w-3xl">
          {/* Hashtags above eyebrow */}
          {slide.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="hero-hashtags">
              {slide.tags.map((t) => (
                <span
                  key={t}
                  className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-white text-[11px] font-semibold tracking-wide drop-shadow-[0_2px_6px_rgba(15,23,42,0.6)]"
                >
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* Animated FEATURED CAPABILITY overline */}
          <motion.div
            key={slideKey + "-overline"}
            initial={{ opacity: 0, y: 14, letterSpacing: "0.4em" }}
            animate={{ opacity: 1, y: 0, letterSpacing: "0.28em" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-[11px] uppercase font-bold text-blue-200 inline-flex items-center gap-2 drop-shadow-[0_2px_8px_rgba(15,23,42,0.7)]"
          >
            <motion.span
              className="inline-block h-px bg-blue-300"
              initial={{ width: 0 }}
              animate={{ width: 36 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
            <Sparkles size={12} className="text-blue-200" />
            Featured Capability · {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            <motion.span
              className="inline-block h-px bg-blue-300"
              initial={{ width: 0 }}
              animate={{ width: 36 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] font-semibold mt-4 text-balance drop-shadow-[0_4px_24px_rgba(15,23,42,0.75)]"
          >
            {slide.title}
          </h1>

          <p
            className="mt-4 text-base md:text-xl text-blue-100 font-display italic drop-shadow-[0_2px_10px_rgba(15,23,42,0.7)]"
            data-testid="hero-tagline"
          >
            <span aria-hidden>&ldquo;</span>
            {slide.tagline}
            <span aria-hidden>&rdquo;</span>
          </p>

          <p
            className="mt-4 text-sm md:text-base text-white/90 leading-relaxed max-w-xl drop-shadow-[0_2px_8px_rgba(15,23,42,0.75)]"
            data-testid="hero-description"
          >
            {slide.description}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href={slide.cta_link || "#projects"}
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 text-sm font-semibold tracking-wide uppercase transition-all shadow-[0_20px_45px_-18px_rgba(37,99,235,0.7)]"
            >
              {slide.cta_label}
              <ArrowUpRight size={16} />
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 text-white hover:bg-white hover:text-[#0F172A] px-6 py-3 text-sm font-semibold tracking-wide uppercase transition-all backdrop-blur-sm"
            >
              Talk to an Expert
            </a>
          </div>

          <div className="mt-6 flex items-center gap-2 text-[10px] text-blue-100 uppercase tracking-[0.22em] drop-shadow-[0_2px_6px_rgba(15,23,42,0.6)]">
            <Sparkles size={12} className="text-blue-200" /> Sovereign AI · Government Grade · Secure
          </div>
        </div>
      </div>

      {/* Floating sub-image indicator */}
      {subImages.length > 1 && (
        <div className="absolute top-32 right-8 z-10 hidden lg:flex flex-col gap-2">
          {subImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setSubIndex(i)}
              data-testid={`hero-sub-${i}`}
              aria-label={`Show preview ${i + 1}`}
              className={`h-10 w-1 rounded-full transition-all ${i === subIndex ? "bg-[#2563EB]" : "bg-white/80 hover:bg-white"}`}
            />
          ))}
        </div>
      )}

      {/* Bottom HUD */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-8 flex items-center justify-between gap-6">
          <div className="hidden md:flex items-center gap-6 text-white/90 text-xs uppercase tracking-[0.25em]">
            <button data-testid="hero-prev" onClick={prev} aria-label="Previous slide" className="hover:text-white flex items-center gap-2">
              <ChevronLeft size={16} /> Prev
            </button>
            <button data-testid="hero-next" onClick={next} aria-label="Next slide" className="hover:text-white flex items-center gap-2">
              Next <ChevronRight size={16} />
            </button>
          </div>
          <div className="flex-1 flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id || s.title}
                onClick={() => { setIndex(i); setSubIndex(0); }}
                data-testid={`hero-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-[3px] flex-1 max-w-[80px] rounded-full transition-all ${i === index ? "bg-white" : "bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
          <div className="hidden md:block text-white/90 text-xs uppercase tracking-[0.25em]">
            Scroll to Explore ↓
          </div>
        </div>
      </div>
    </section>
  );
}
